import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import Settings from "Data/Settings";
import * as Topics from "Data/Topics";
import * as VO from "Data/VoiceOver";
import * as SFX from "Data/SoundEffect";
import * as Constants from "Data/Constants";
import { Elastic, Sine, TimelineLite, TimelineMax, TweenLite } from "gsap";
import { OutlineFilter } from "Filters/OutlineFilter";
import { DuckSwapGame } from "States/DuckSwapGame";
import { IDuckConfig, IDuckState, IRuntimeDuck } from "Data/GameState";

export class SwapGameDuck extends HLF.Input.Selectable implements IRuntimeDuck {

    public readonly sprite: PIXI.Sprite;
    public readonly energy: number;
    public readonly efficiency: number; // unused in swap game

    public readonly config: IDuckConfig;

    protected dwellTimeline?: TimelineLite;
    protected isCursorDwelling: boolean = false;

    protected _hintTimeline?: TimelineMax;
    protected isOver: boolean = false;

    public constructor(pConfig: IDuckConfig, pData: IDuckState) {
        super();
        this.sprite = this._visuals.addChild(HLF.PixiUtils.makeSprite(pData.energy < 75 ? pConfig.swapSprite_tired : pConfig.swapSprite));
        this.sprite.scale.set(pConfig.scale ?? 1);
        this.hitArea = HLF.Input.hitAreaFromSprite(this.sprite);

        this.name = pData.name;
        this.energy = pData.energy;
        this.efficiency = pData.efficiency;

        this.config = pConfig;

        this._clickedSfx = SFX.UI_CLICK;
        this._hoverVo = pConfig.nameVO_swap;

        this._outlineFilter = new OutlineFilter(HLF.PlatformUtils.isMobile() ? 5 : 10, 0xffffff);
    }

    public get visuals() { return this._visuals; };

    public toggleSelected() {
        // never deselect
        if (this._isSelected) {
            return;
        }
        else {
            super.toggleSelected();
        }
    }

    public onFocusBegin(): void {
        // don't call super.onFocusBegin
        this.playKeyboardFocusVO();
    }

    public pulse(scale: number = 1.15, interval: number = 1.5, startImmediately: boolean = true) {
        this._hintTimeline?.kill();
        this._hintTimeline = new TimelineMax({repeat: -1});
        this._hintTimeline
            .to(this, 0.15, {
                pixi: {
                    scale: 1 * scale
                },
                ease: Sine.easeInOut,
            }, interval)
            .call(()=>{
                this.filters = [this._outlineFilter];
            })
            .to(this, 1.0, {
                pixi: {
                    scale: 1,
                },
                ease: Elastic.easeOut,
            })
            .call(()=>{
                this._hintTimeline?.seek(0);
                if (this.isOver) {
                    this._hintTimeline?.pause();
                }
                else if (!this.isSelected) {
                    this.filters = [];
                }
            })

        if (startImmediately) {
            this._hintTimeline.seek(interval);
        }
    }

    public stopPulse() {
        this._hintTimeline?.kill();
        this._hintTimeline?.clear();
        this._hintTimeline?.to(this, 0.15, {pixi: {scale: 1}, ease: Elastic.easeOut})
            .call(()=>{
                if (!this.isSelected) {
                    this.filters = [];
                }
            });
    }

    public destroy(pOptions?: Parameters<typeof PIXI.Container.prototype.destroy>[0]): void {
        super.destroy(pOptions);
        this._hintTimeline?.kill();
    }

    protected playHoverVo(): void {
        // don't call super.playHoverVo()
        if (this._hoverVo !== undefined) {
            HLF.Application.instance.voiceover.playVO(this._hoverVo, { priority: -2, mode: HLF.PlayMode.Override });
        }
    }

    protected playKeyboardFocusVO(): void {
        if (this._hoverVo !== undefined) {
            if (Settings.audioDescriptionEnabled) {
                const vosToPlay = [];
                vosToPlay.push(this.config.nameVO_swap);
                vosToPlay.push(VO.SWAP_AUDIO_DESCRIPTION_DUCK_HOVER);
                const duckEnergyOutOfFive = Math.floor(this.energy / 20) || Math.round(this.energy / 20); // if it's less than 1/5, round instead of floor

                let numberVO: string;
                switch (Settings.difficulty) {
                    case 0 / 3: // adaptive difficulty
                    case 1 / 3:
                        switch (duckEnergyOutOfFive) {
                            case 0:
                                numberVO = VO.NUMBER_ZERO;
                                break;
                            case 1:
                                numberVO = VO.NUMBER_ONE;
                                break;
                            case 2:
                                numberVO = VO.NUMBER_TWO;
                                break;
                            case 3:
                                numberVO = VO.NUMBER_THREE;
                                break;
                            case 4:
                                numberVO = VO.NUMBER_FOUR;
                                break;
                            case 5:
                                numberVO = VO.NUMBER_FIVE;
                                break;
                        }
                        break;
                    case 2 / 3:
                        switch (duckEnergyOutOfFive) {
                            case 0:
                                numberVO = VO.NUMBER_ZERO;
                                break;
                            case 1:
                                numberVO = VO.NUMBER_FIVE;
                                break;
                            case 2:
                                numberVO = VO.NUMBER_TEN;
                                break;
                            case 3:
                                numberVO = VO.NUMBER_FIFTEEN;
                                break;
                            case 4:
                                numberVO = VO.NUMBER_TWENTY;
                                break;
                            case 5:
                                numberVO = VO.NUMBER_TWENTYFIVE;
                                break;
                        }
                        break;
                    case 3 / 3:
                        switch (duckEnergyOutOfFive) {
                            case 0:
                                numberVO = VO.NUMBER_ZERO;
                                break;
                            case 1:
                                numberVO = VO.NUMBER_TEN;
                                break;
                            case 2:
                                numberVO = VO.NUMBER_TWENTY;
                                break;
                            case 3:
                                numberVO = VO.NUMBER_THIRTY;
                                break;
                            case 4:
                                numberVO = VO.NUMBER_FORTY;
                                break;
                            case 5:
                                numberVO = VO.NUMBER_FIFTY;
                                break;
                        }
                        break;
                }
                vosToPlay.push(numberVO!);
                HLF.Application.instance.voiceover.playVO(vosToPlay, {mode: HLF.PlayMode.Override, priority: -1, caption:{
                    id: VO.SWAP_AUDIO_DESCRIPTION_DUCK_HOVER,
                    args: {"DUCK": this.config.nameVO_swap, "NUMBER": numberVO!}
                }});
            }
            else {
                this.playHoverVo();
            }
        }
    }

    protected playClickedSFX(): void {
        // don't call super.playClickedSFX();
        if (this._clickedSfx !== undefined) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(this._clickedSfx, 1, false, HLF.AudioCategory.SFX));
        }
    }

    protected onPointerOver(pEvent: PIXI.InteractionEvent): void {
        super.onPointerOver(pEvent);
        if (Settings.useDwellActivation) {
            this.beginDwell();
        }
        this.isOver = true;
    }

    protected onPointerDown(pEvent: PIXI.InteractionEvent): void {
        super.onPointerDown(pEvent);
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
    }

    protected onPointerUp(pEvent: PIXI.InteractionEvent): void {
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
        super.onPointerUp(pEvent);
    }

    protected onPointerOut(pEvent: PIXI.InteractionEvent): void {
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
        super.onPointerOut(pEvent);
        this.isOver = false;
        this._hintTimeline?.resume();
    }


    protected beginDwell() {
        this.isCursorDwelling = true;
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        this.dwellTimeline = new TimelineLite();
        this.dwellTimeline.call(()=>PubSub.publishSync(Topics.DWELL_BEGIN, this), undefined, undefined, 0);
        this.dwellTimeline.call(()=>this.completeDwell(), undefined, undefined, Constants.DWELL_DURATION);
    }

    protected cancelDwell(): void {
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        if (this.isCursorDwelling) {
            this.isCursorDwelling = false;
            PubSub.publishSync(Topics.DWELL_CANCELLED, this);
        }
    }

    protected completeDwell(): void {
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        PubSub.publishSync(Topics.DWELL_ACTIVATED, this);
        this.isCursorDwelling = false;
        this.onFocusActivated(); // TODO: swap texture to down texture?
    }

}